import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Wind, Brain, Heart, Moon, Utensils, Thermometer, Bed } from 'lucide-react';
const pillars = [{
  icon: Activity,
  title: 'Pilates Core Science',
  desc: 'Mechanically decompresses the lumbar spine safely.'
}, {
  icon: Heart,
  title: 'Yin Yoga Fascial Release',
  desc: 'Releases deep connective tissue tension holding the pain.'
}, {
  icon: Brain,
  title: 'Somatic Re-patterning',
  desc: "Teaches the nervous system to exit 'threat mode'."
}, {
  icon: Wind,
  title: 'Vagus Nerve Activation',
  desc: 'Uses specific breath patterns as a biological pain off-switch.'
}];
export function MechanismReveal() {
  return <section className="py-24 px-6 bg-navy-900" data-id="element-275">
      <div className="max-w-5xl mx-auto" data-id="element-276">
        <div className="text-center space-y-6 mb-16" data-id="element-277">
          <h2 className="text-3xl md:text-5xl font-serif text-cream leading-tight" data-id="element-278">
            What if you could break all four points of the loop in under 12
            minutes?
          </h2>
          <p className="text-xl text-cream-300/80 max-w-3xl mx-auto" data-id="element-279">
            Not with medication. Not with expensive equipment. Not with a
            45-minute yoga class you don't have energy for at 11PM.
          </p>
          <p className="text-lg text-cream-300/70" data-id="element-280">
            But with a precise sequence — developed from the intersection of
            four evidence-based disciplines:
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-281">
          {pillars.map((pillar, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-navy-800 border border-navy-700 p-6 rounded-2xl text-center hover:border-teal-500/50 transition-colors" data-id="element-282">
              <div className="w-12 h-12 mx-auto bg-teal-500/10 rounded-full flex items-center justify-center mb-4 text-teal-400" data-id="element-283">
                <pillar.icon className="w-6 h-6" data-id="element-284" />
              </div>
              <h3 className="text-lg font-bold text-cream mb-2" data-id="element-285">
                {pillar.title}
              </h3>
              <p className="text-sm text-cream-300/70" data-id="element-286">{pillar.desc}</p>
            </motion.div>)}
        </div>

        <div className="text-center space-y-8" data-id="element-287">
          <p className="text-lg text-cream-300/80 max-w-2xl mx-auto" data-id="element-288">
            When these four work together in a specific sequence... Your muscles
            release their guarding reflex. Your inflammation response drops.
            Your nervous system exits "threat mode." Your pain perception
            normalizes.
          </p>
          <p className="text-2xl font-serif text-cream italic" data-id="element-289">
            And your body finally gets the signal it's been desperately waiting
            for: <br data-id="element-290" />
            <span className="text-teal-400 font-bold not-italic" data-id="element-291">
              It's safe to sleep now.
            </span>
          </p>

          <div className="mt-12 inline-block" data-id="element-292">
            <p className="text-sm text-cream-300/60 uppercase tracking-widest mb-2" data-id="element-293">
              This combination is what we call:
            </p>
            <div className="bg-gradient-to-r from-teal-600/20 via-teal-500/20 to-teal-600/20 border border-teal-500/30 px-8 py-6 rounded-2xl" data-id="element-294">
              <h3 className="text-3xl md:text-4xl font-serif text-teal-400" data-id="element-295">
                THE SOMATIC FUSION METHOD™
              </h3>
            </div>
            <p className="mt-4 text-cream-300/80 max-w-xl mx-auto" data-id="element-296">
              The first and only protocol specifically designed to interrupt the
              nighttime pain loop at all four points — in 12 minutes — from the
              floor of your bedroom.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
export function ProductIntro() {
  return <section className="py-24 px-6 bg-navy-800 border-t border-navy-700" data-id="element-297">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-298">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} className="relative" data-id="element-299">
          <div className="absolute inset-0 bg-teal-500/20 blur-[80px] rounded-full" data-id="element-300" />
          <img src="https://cdn.magicpatterns.com/uploads/mdPNgk2XAGWNJRTnFmS9qf/The_11pm_Spine_Reset_eBook_Cover.png" alt="The 11PM Spine Reset" className="relative z-10 w-full max-w-md mx-auto rounded-xl shadow-2xl border border-white/10" data-id="element-301" />
        </motion.div>

        <div className="space-y-8" data-id="element-302">
          <div data-id="element-303">
            <p className="text-teal-400 font-bold tracking-widest uppercase text-sm mb-4" data-id="element-304">
              Introducing
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-cream leading-tight mb-4" data-id="element-305">
              The 11PM Spine Reset
            </h2>
            <p className="text-xl text-cream-300/90 font-medium" data-id="element-306">
              The 12-Minute Holistic Protocol That Stops Chronic Back Pain &
              Restores Deep Sleep — Tonight
            </p>
          </div>

          <div className="space-y-4 text-cream-300/80" data-id="element-307">
            <p data-id="element-308">
              This is not a stretching guide. This is not a yoga book. This is
              not another "try these 5 exercises" PDF.
            </p>
            <p data-id="element-309">
              This is a complete, sequenced, science-backed nightly protocol —
              built specifically for people whose back pain peaks at night and
              steals their sleep.
            </p>
          </div>

          <ul className="space-y-3 bg-navy-900/50 p-6 rounded-xl border border-navy-700" data-id="element-310">
            <li className="flex items-start gap-3" data-id="element-311">
              <span className="text-teal-400 mt-1" data-id="element-312">✓</span>
              <span data-id="element-313">It works if you're 28 or 78.</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-314">
              <span className="text-teal-400 mt-1" data-id="element-315">✓</span>
              <span data-id="element-316">
                It works if you've had back pain for 3 weeks or 13 years.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-317">
              <span className="text-teal-400 mt-1" data-id="element-318">✓</span>
              <span data-id="element-319">
                It works if you've tried everything else and feel hopeless.
              </span>
            </li>
          </ul>

          <p className="text-xl font-serif text-teal-400 italic" data-id="element-320">
            Because it's the first approach that doesn't attack one point of the
            loop. It destroys the entire loop. Every night.
          </p>
        </div>
      </div>
    </section>;
}
const chapters = [{
  num: '1',
  icon: Moon,
  title: 'The Midnight Threshold',
  tagline: "Why Your Pain Peaks At Night — And Why That's Actually Good News",
  desc: "You'll finally understand the exact biological reason your pain is worst at bedtime. And why understanding this mechanism is the first step to ending it permanently."
}, {
  num: '2',
  icon: Wind,
  title: 'The Vagus Key',
  tagline: 'The SOS Breathing Pattern That Switches Pain Off In 90 Seconds',
  desc: "Your body has a built-in pain off-switch. It's called the vagus nerve. This chapter gives you the exact 4-7-8 protocol that down-regulates your pain response before you even begin."
}, {
  num: '3',
  icon: Activity,
  title: 'The 7-Minute Somatic Sequence',
  tagline: 'The Floor Protocol That Decompresses Your Spine',
  desc: "The heart of the system. A precisely ordered series of movements that mechanically decompresses your lumbar spine while simultaneously releasing the nervous system's guarding reflex."
}, {
  num: '4',
  icon: Utensils,
  title: 'Nutritional First Aid',
  tagline: 'What You Eat Is Either Fueling Or Fighting Your Back Pain',
  desc: 'The exact anti-inflammatory food framework, the 7-day spine healing meal plan, and the nightly Spine Reset Tonic recipe that reduces your inflammatory load while you sleep.'
}, {
  num: '5',
  icon: Thermometer,
  title: 'Temperature & Touch Therapy',
  tagline: 'The Contrast Cycling Protocol + Self-Acupressure Map',
  desc: 'Learn the exact heat vs cold decision guide, the 5-minute self-massage protocol using a tennis ball, and the 5-point acupressure map that targets lumbar pain relief.'
}, {
  num: '6',
  icon: Bed,
  title: 'The Sleep-Ready Environment',
  tagline: 'Transform Your Bedroom Into A Spine Healing Sanctuary',
  desc: "The 3-minute Pain Dissolve Meditation. The Spine-Safe Sleep Position Guide. Because the work doesn't end when you finish the sequence."
}];
export function ChaptersGrid() {
  return <section className="py-24 px-6 bg-navy-900" data-id="element-321">
      <div className="max-w-6xl mx-auto" data-id="element-322">
        <div className="text-center mb-16" data-id="element-323">
          <h2 className="text-4xl md:text-5xl font-serif text-cream mb-4" data-id="element-324">
            What's Inside
          </h2>
          <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full" data-id="element-325" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-326">
          {chapters.map((ch, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-navy-800 border border-navy-700 rounded-2xl p-8 relative overflow-hidden group hover:border-teal-500/50 transition-colors" data-id="element-327">
              <div className="absolute top-0 right-0 text-9xl font-serif text-navy-900/50 font-black -mt-8 -mr-4 pointer-events-none" data-id="element-328">
                {ch.num}
              </div>
              <div className="relative z-10" data-id="element-329">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center text-teal-400 mb-6" data-id="element-330">
                  <ch.icon className="w-6 h-6" data-id="element-331" />
                </div>
                <h3 className="text-sm font-bold text-teal-400 tracking-widest uppercase mb-2" data-id="element-332">
                  Chapter {ch.num}
                </h3>
                <h4 className="text-2xl font-serif text-cream mb-3" data-id="element-333">
                  {ch.title}
                </h4>
                <p className="text-cream-300/90 font-medium italic mb-4" data-id="element-334">
                  {ch.tagline}
                </p>
                <p className="text-cream-300/70 text-sm leading-relaxed" data-id="element-335">
                  {ch.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}